/**
 * Images a Super Admin has put on the site through Settings → Image Management.
 *
 * These replaced an array of twenty-one entries hardcoded in HomePage.tsx, where adding a
 * photograph from the last office party meant a source edit, a review, a build and a deploy.
 */

/**
 * Where a managed image appears.
 *
 * A closed set rather than free text, and it is the SAME set the Lambda validates against: a
 * placement nothing renders is an image a Super Admin uploaded, saw accepted, and will never find
 * on the site. Adding one is a line here, a line there, and a component that reads it.
 */
export enum ImagePlacement {
  HOME_CAROUSEL = 'home-carousel',
}

/** What each placement is called where a human has to pick one, and what it does. */
export const PLACEMENT_LABELS: Record<ImagePlacement, { label: string; hint: string }> = {
  [ImagePlacement.HOME_CAROUSEL]: {
    label: 'Home page carousel',
    hint: 'The rotating gallery every employee sees on the home page.',
  },
};

export const ALL_PLACEMENTS: ImagePlacement[] = Object.values(ImagePlacement);

export interface WebsiteImage {
  id: string;
  placement: ImagePlacement | string;
  name: string;
  description?: string;
  /**
   * An object in the uploads bucket, for an image somebody uploaded here.
   *
   * Exactly one of this and `sourcePath` is set. A storageKey has to be signed for before it can
   * be displayed, which is why the list and the URLs are fetched separately — see
   * websiteImageService.resolveImageUrls.
   */
  storageKey?: string;
  /**
   * A path served by the frontend build, like `/images/teamlunch.jpg`.
   *
   * How the twenty-one inherited carousel images are described. They are files in the public
   * folder, so they need no signing and are rendered as-is. Replacing one through the UI uploads
   * the replacement and the row becomes a storageKey row.
   */
  sourcePath?: string;
  displayOrder: number;
  active: boolean;
  createdAt?: string;
  createdBy?: string;
  updatedAt?: string;
  updatedBy?: string;
}

/** What the page sends to create or update one. `id` absent creates. */
export interface WebsiteImageInput {
  id?: string;
  placement: ImagePlacement | string;
  name: string;
  description?: string;
  storageKey?: string;
  sourcePath?: string;
  active?: boolean;
}

/**
 * An image plus the URL that actually renders it.
 *
 * Kept apart from WebsiteImage because the URL is not part of the record: a signed one expires,
 * so persisting it would work until it quietly stopped. This is the shape a component holds.
 */
export interface ResolvedWebsiteImage extends WebsiteImage {
  /** The src to render, or '' when the file could not be signed for. */
  url: string;
}
